import { useState, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Stepper, Step, StepLabel, Grid, MenuItem, Select, SelectChangeEvent, InputLabel, FormControl, Stack, FormHelperText, Box, Popover, Typography, IconButton, Alert, InputAdornment } from '@mui/material';
import { useParams } from "react-router-dom";
import axios from 'axios';
import { useSignal } from '@preact/signals-react';
import * as Yup from 'yup';
import InfoIcon from '@mui/icons-material/Info';
import AlertTitle from '@mui/material/AlertTitle';
import { VisibilityOff, Visibility } from '@mui/icons-material';

const validationSchema = Yup.object().shape({
	institutionName: Yup.string().required('Institution Name is required'),
	institutionType: Yup.string().notOneOf(['0'], 'Institution Type is required'),
	institutionMainPhone: Yup.string().required('Institution Main Phone is required').max(25, 'Phone number should not be more than 25 characters.'),
	institutionGroupContactEmail: Yup.string().email('Invalid email').required('Institution Group Contact Email is required'),
	addressLine1: Yup.string().required('Address Line 1 is required'),
	addressLine2: Yup.string(),
	addressLine3: Yup.string(),
	city: Yup.string().required('City is required'),
	stateProvince: Yup.string().when('country', {
		is: (country: string) => ['UNITED STATES', 'CANADA'].includes(country),
		then: (schema) => schema.required('State/Province is required'),
		otherwise: (schema) => schema.optional()
	}),
	country: Yup.string().required('Country is required'),
	postalCode: Yup.string()
		.when('country', {
			is: (country: string) => country === 'UNITED STATES',
			then: (schema) =>
				schema
					.required('Postal/ZIP Code is required')
					.max(10, 'Zip/Postal code should not be more than 10 characters.')
					.matches(
						/^[0-9]{5}(?:-[0-9]{4})?$/,
						'Invalid ZIP code format. Use 99999 or 99999-9999.'
					),
		})
		.when('country', {
			is: (country: string) => country === 'CANADA',
			then: (schema) =>
				schema
					.required('Postal/ZIP Code is required')
					.max(10, 'Zip/Postal code should not be more than 10 characters.')
					.matches(
						/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
						'Invalid Canadian postal code format. Use A1A 1A1.'
					),
		})
		.when('country', {
			is: (country: string) => !['UNITED STATES', 'CANADA'].includes(country),
			then: (schema) => schema.optional(),
		}),
	firstName: Yup.string().required('First Name is required'),
	lastName: Yup.string().required('Last Name is required'),
	email: Yup.string().email('Invalid email').required('Email is required'),
	directPhone: Yup.string().required('Direct Phone is required').max(25, 'Phone number should not be more than 25 characters.'),
	extension: Yup.string(),
	password: Yup.string()
		.required('Password is required')
		.min(6, 'The password must be at least 6 characters long.')
		.max(100, 'The password must be at most 100 characters long.')
		.matches(
			/^(?=.*[A-Za-z])(?=.*[A-Z])(?=.*\d)(?=.*[!"@$*#&'()+\-,.:;<>?[\]\\~^_/])[A-Za-z\d!"@$*#&'()+-,.:;<>?[\]\\~^_\/]{6,}$/,
			'Passwords must contain a minimum of 6 characters with at least one uppercase letter, one lowercase letter, one number, and one special character (!"@$*#&\'()+-,.:;<>?[\\]~^_/).'
		),
	confirmPassword: Yup.string()
		.required('Please confirm your password')
		.oneOf([Yup.ref('password')], 'Passwords must match')
});

const Register = () => {
	const [activeStep, setActiveStep] = useState(0);
	const [open, setOpen] = useState(true);
	const [formData, setFormData] = useState({
		institutionName: '',
		institutionType: '0',
		institutionMainPhone: '',
		institutionGroupContactEmail: '',
		addressLine1: '',
		addressLine2: '',
		addressLine3: '',
		city: '',
		stateProvince: '',
		country: 'UNITED STATES',
		postalCode: '',
		firstName: '',
		lastName: '',
		email: '',
		directPhone: '',
		extension: '',
		password: '',
		confirmPassword: ''
	});
	const [errors, setErrors] = useState<{ [key: string]: string }>({});

	let { email } = useParams<{ email: string }>();

	useEffect(() => {
		if (email) {
			setFormData((prevFormData) => ({
				...prevFormData,
				email: email
			}));
		}
	}, [email]);

	interface countryState {
		stateName: string,
		stateAbbreviation: string,
		countryName: string
	}

	const countries = useSignal<string[]>([]);
	const countryStates = useSignal<countryState[]>([]);

	useEffect(() => {
		if (!document.referrer.toLowerCase().includes("preregister")) {
			throw new Error("UX Flow Issue: User accessed the registration page without preregistering.");
		}

		let retrieveCountries = async () => {
			try {
				let response = await axios.get('/api/common/getCountries');
				countries.value = response.data;
			}
			catch { }
		}

		let retrieveCountryStates = async () => {
			try {
				let response = await axios.get('/api/common/getCountryStates');
				countryStates.value = response.data;
			}
			catch { }
		}

		retrieveCountries();
		retrieveCountryStates();
	}, [countries, countryStates]);

	const steps = ['Institutional Profile', 'User Profile'];

	const validateStep = async (step: number) => {
		try {
			let fieldsToValidate: string[] = [];

			if (step === 0) {
				fieldsToValidate = [
					'institutionName',
					'institutionType',
					'institutionMainPhone',
					'institutionGroupContactEmail',
					'addressLine1',
					'city',
					'stateProvince',
					'country',
					'postalCode'
				];
			} else if (step === 1) {
				fieldsToValidate = [
					'firstName',
					'lastName',
					'email',
					'directPhone',
					'password',
					'confirmPassword'
				];
			}

			const stepSchema = Yup.object().shape(
				Object.fromEntries(
					fieldsToValidate.map(field => [field, validationSchema.fields[field]])
				)
			);

			await stepSchema.validate(formData, { abortEarly: false });
			setErrors({});
			return true;
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				const newErrors: { [key: string]: string } = {};
				err.inner.forEach((error) => {
					if (error.path) {
						newErrors[error.path] = error.message;
					}
				});
				setErrors(newErrors);
			}
			return false;
		}
	};

	const handleNext = async () => {
		const isValid = await validateStep(activeStep);
		if (isValid) {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
		}
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		// Clear error when field is modified
		if (errors[name]) {
			setErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const handleSelectChange = (e: SelectChangeEvent) => {
		const { name, value } = e.target;
		if (name === "country") {
			setFormData(prev => ({
				...prev,
				[name]: value,
				stateProvince: ""
			}));
		} else {
			setFormData(prev => ({
				...prev,
				[name]: value
			}));
		}
		// Clear error when field is modified
		if (errors[name]) {
			setErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};
	function cleanPhoneAndPostalCharacters() {
		formData.directPhone = formData.directPhone.replace(/\+|\(|\)|-|\s/g, "");
		formData.institutionMainPhone = formData.institutionMainPhone.replace(/\+|\(|\)|-|\s/g, "");
		formData.postalCode = formData.postalCode.replace(/\s|-/g, "");
	}
	interface ISubmissionState {
		success: boolean;
		failure: boolean;
		message: string;
	}

	const [submissionState, setSubmissionState] = useState({} as ISubmissionState);
	const [isClosing, setIsClosing] = useState(false);

	function sleep(ms: number) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	const handleSubmit = async () => {
		try {
			await validationSchema.validate(formData, { abortEarly: false });
			cleanPhoneAndPostalCharacters();
			await axios.post('api/auth/register', formData);
			setSubmissionState({
				success: true,
				failure: false,
				message: 'Registration Success!'
			});

			setIsClosing(true);
			await sleep(3000);
			setOpen(false);
			window.location.href = '/';
		} catch (err: any) {
			if (err instanceof Yup.ValidationError) {
				const newErrors: { [key: string]: string } = {};

				err.inner.forEach((error) => {
					if (error.path) {
						newErrors[error.path] = error.message;
					}
				});

				setErrors(newErrors);
				return;
			}

			setSubmissionState({
				success: false,
				failure: true,
				message: "Failed to register the new user.  Please try again later."
			});

			setIsClosing(true);
		}
	};

	const handleClose = (event: React.MouseEventHandler<HTMLButtonElement>, reason: string) => {
		if (reason === 'backdropClick') {
			return;
		}
		setOpen(false);
		window.location.href = '/';
	};

	const handleCancel = () => {
		setOpen(false);
		window.location.href = '/';
	};

	// Helper function to get error props for TextField components
	const getErrorProps = (fieldName: string) => ({
		error: !!errors[fieldName],
		helperText: errors[fieldName]
	});

	// Add a style object for consistent form control styling
	const formControlStyle = {
		margin: '0.5rem',
		width: '100%',
		'& .MuiInputLabel-root': {
			transform: 'translate(0, -1.5px) scale(0.75)',
			transformOrigin: 'top left'
		},
		'& .MuiInputLabel-root.Mui-focused': {
			transform: 'translate(0, -1.5px) scale(0.75)',
		},
		'& .MuiInputBase-root': {
			marginTop: '16px'
		}
	};

	const [anchorEl, setAnchorEl] = useState(null);
	const [faqContent, setFaqContent] = useState("");

	const fetchFaqContent = async () => {
		try {
			const response = await axios.post("api/common/GetStringsByKeys", ["institutionEmailTitle"]);

			// Assuming response.data is an array of strings
			if (Array.isArray(response.data) && response.data.length > 0) {
				setFaqContent(response.data[0].value); // Set faqContent to the first string
			} else {
				console.warn("Response data is not a valid array or is empty.");
				setFaqContent("No content available."); // Fallback content
			}
		} catch (error) {
			// Handle errors
			console.error("Error fetching strings by keys:", error);
			setFaqContent("Error loading content."); // Fallback content in case of error
		}
	};

	const handleIconClick = (event: any) => {
		setAnchorEl(event.currentTarget);
		fetchFaqContent();
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
	};

	const popoverOpen = Boolean(anchorEl);
	const id = popoverOpen ? "faq-popover" : undefined;

	// Custom label component with info icon.
	const renderLabel = () => (
		<Box
			display="flex"
			alignItems="center"
			component="label"
			htmlFor="institutionGroupContactEmail"
			sx={{ whiteSpace: "nowrap" }} // Prevent wrapping
		>
			<span>Institution Group Contact Email</span>

			<Typography
				component="span"
				sx={{ ml: 0.5, fontSize: "1rem", lineHeight: 1 }}
			>
				*
			</Typography>

			<IconButton
				size="small"
				onClick={handleIconClick}
				aria-describedby={id}
				edge="end"
				sx={{ ml: 0.5 }}
			>
				<InfoIcon fontSize="small" />
			</IconButton>
		</Box>
	);

	const [showPasswords, setShowPasswords] = useState(false);

	const handleClickShowPasswords = () => {
		setShowPasswords(!showPasswords);
	}

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="form-dialog-title"
			BackdropProps={{ onClick: (e) => e.stopPropagation() }}
			fullWidth
			maxWidth="lg"
		>
			<DialogTitle id="form-dialog-title">Register for a New Account</DialogTitle>

			<DialogContent>
				<Stepper activeStep={activeStep} style={{ "marginBottom": "2rem" }}>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>

				{activeStep === 0 && (
					<>
						<p style={{ "fontWeight": "lighter" }}>Welcome to the Registration Request form for Epiq Filing. This site is intended for Nominees and Third-Party Filers on behalf of shareholders in securities cases administered by Epiq. Registering for an account will allow you to request filing templates and submit completed templates on behalf of your clients/customers. Once registered, you will not need to register again and you will have access to all cases Epiq is administering through the site.</p>

						<Stack direction="row" style={{ "marginBottom": "1rem" }}>
							<p style={{ "fontWeight": "bolder" }}>Step 1: Institutional Profile</p>
							<p style={{ "fontWeight": "bold" }}>Create an institutional profile by filling out the following information. New institutional profiles are usually approved within one to two business days.</p>
						</Stack>

						<Grid container spacing={2}>
							<Grid item xs={6}>
								<TextField
									autoFocus
									margin="dense"
									name="institutionName"
									label="Institution Name"
									type="text"
									fullWidth
									value={formData.institutionName}
									onChange={handleChange}
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									required
									{...getErrorProps('institutionName')}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormControl
									error={!!errors.institutionType}
									variant='standard'
									sx={formControlStyle}
								>
									<InputLabel
										id="institutionType-label"
										required
										shrink={true}
										style={{
											color: "black"
										}}
									>
										Institution Type
									</InputLabel>
									<Select
										labelId="institutionType-label"
										id="institutionType"
										name="institutionType"
										value={formData.institutionType}
										onChange={handleSelectChange}
										label="Institution Type"
									>
										<MenuItem value="0" hidden></MenuItem>
										<MenuItem value="1">Broker/Nominee</MenuItem>
										<MenuItem value="2">Bank</MenuItem>
										<MenuItem value="3">Law Firm</MenuItem>
										<MenuItem value="4">Third Party (Claim Preparer)</MenuItem>
										<MenuItem value="5">Investment Manager</MenuItem>
										<MenuItem value="6">Trust Company</MenuItem>
										<MenuItem value="7">Other</MenuItem>
									</Select>
									{errors.institutionType && <FormHelperText>{errors.institutionType}</FormHelperText>}
								</FormControl>
							</Grid>
							<Grid item xs={6}>
								<TextField
									margin="dense"
									name="institutionMainPhone"
									label="Institution Main Phone"
									type="tel"
									fullWidth
									value={formData.institutionMainPhone}
									onChange={handleChange}
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									required
									{...getErrorProps('institutionMainPhone')}
								/>
							</Grid>

							<Grid item xs={6}>
								<TextField
									id="institutionGroupContactEmail"
									margin="dense"
									name="institutionGroupContactEmail"
									label={renderLabel()}
									type="email"
									fullWidth
									value={formData.institutionGroupContactEmail}
									onChange={handleChange}
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									{...getErrorProps("institutionGroupContactEmail")}
								/>

								<Popover
									id={id}
									open={popoverOpen}
									anchorEl={anchorEl}
									onClose={handlePopoverClose}
									anchorOrigin={{
										vertical: "bottom",
										horizontal: "left"
									}}
								>
									<Box p={2} maxWidth={300}>
										<Typography variant="h6">FAQ</Typography>
										<Typography variant="body2" mt={1}>
											{faqContent}
										</Typography>
									</Box>
								</Popover>
							</Grid>

							<Grid item xs={6}>
								<TextField
									margin="dense"
									name="addressLine1"
									label="Address Line 1"
									type="text"
									fullWidth
									value={formData.addressLine1}
									onChange={handleChange}
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									required
									{...getErrorProps('addressLine1')}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									margin="dense"
									name="addressLine2"
									label="Address Line 2"
									type="text"
									fullWidth
									value={formData.addressLine2}
									onChange={handleChange}
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									{...getErrorProps('addressLine2')}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									margin="dense"
									name="addressLine3"
									label="Address Line 3"
									type="text"
									fullWidth
									value={formData.addressLine3}
									onChange={handleChange}
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									{...getErrorProps('addressLine3')}
								/>
							</Grid>
							<Grid item xs={6} sm={6}>
								<FormControl
									error={!!errors.country}
									variant="standard"
									sx={formControlStyle}
								>
									<InputLabel
										id="country-label"
										required
										shrink={false}
										style={{
											color: "black"
										}}
									>
										Country
									</InputLabel>
									<Select
										labelId="country-label"
										id="country"
										name="country"
										value={formData.country}
										onChange={handleSelectChange}
										label="Country"
									>
										<MenuItem value="" hidden><em style={{ "fontWeight": "lighter" }}>Select</em></MenuItem>
										{countries.value.map((country) => (
											<MenuItem key={country} value={country}>{country}</MenuItem>
										))}
									</Select>
									{errors.country && <FormHelperText>{errors.country}</FormHelperText>}
								</FormControl>
							</Grid>
							<Grid item xs={4} sm={4}>
								<TextField
									margin="dense"
									name="city"
									label="City"
									type="text"
									fullWidth
									value={formData.city}
									onChange={handleChange}
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									required
									{...getErrorProps('city')}
								/>
							</Grid>

							<Grid item xs={4} sm={4}>
								<FormControl
									error={!!errors.stateProvince}
									variant="standard"
									sx={formControlStyle}
								>
									<InputLabel
										id="stateProvince-label"
										required
										shrink={false}
										style={{
											color: "black"
										}}
									>
										State/Province
									</InputLabel>
									<Select
										labelId="stateProvince-label"
										id="stateProvince"
										name="stateProvince"
										value={formData.stateProvince}
										onChange={handleSelectChange}
										label="State/Province"
									>
										<MenuItem value="" hidden><em style={{ "fontWeight": "lighter" }}>Select</em></MenuItem>
										{countryStates.value
											.filter((state) => state.countryName.toLowerCase() === formData.country.toLowerCase())
											.map((state) => (
												<MenuItem key={state.stateName} value={state.stateName}>{state.stateName}</MenuItem>
											))
										}
									</Select>
									{errors.stateProvince && <FormHelperText>{errors.stateProvince}</FormHelperText>}
								</FormControl>
							</Grid>

							<Grid item xs={4} sm={4}>
								<TextField
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									margin="dense"
									name="postalCode"
									label="Zip/Postal"
									type="text"
									fullWidth
									value={formData.postalCode}
									onChange={handleChange}
									{...getErrorProps('postalCode')}
								/>
							</Grid>
						</Grid>
					</>
				)}

				{activeStep === 1 && (
					<fieldset disabled={isClosing} style={{ border: "none", padding: 0 }}>
						<Stack direction="row">
							<p style={{ "fontWeight": "bolder" }}>Step 2: User Profile</p>
							<p style={{ "fontWeight": "bold" }}>Create your user profile by filling out the following information. You must enter a valid email address to receive communication from Epiq Securities.</p>
						</Stack>

						<p style={{ "fontWeight": "lighter", "marginBottom": "1rem" }}>This is the contact information for your personal profile on the site. Communications between you and Epiq will be sent through the email addresses and phone numbers provided, when applicable.</p>

						<Grid container spacing={2}>
							<Grid item xs={6} sm={6}>
								<TextField
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									autoFocus
									margin="dense"
									name="firstName"
									label="First Name"
									type="text"
									fullWidth
									value={formData.firstName}
									onChange={handleChange}
									required
									{...getErrorProps('firstName')}
								/>
							</Grid>
							<Grid item xs={6} sm={6}>
								<TextField
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									margin="dense"
									name="lastName"
									label="Last Name"
									type="text"
									fullWidth
									value={formData.lastName}
									onChange={handleChange}
									required
									{...getErrorProps('lastName')}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									margin="dense"
									name="email"
									label="Email Address"
									type="email"
									fullWidth
									value={formData.email}
									onChange={handleChange}
									required
									disabled
								/>
							</Grid>
							<Grid item xs={4} sm={4}>
								<TextField
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									margin="dense"
									name="directPhone"
									label="Direct Phone"
									type="tel"
									fullWidth
									value={formData.directPhone}
									onChange={handleChange}
									required
									{...getErrorProps('directPhone')}
								/>
							</Grid>
							<Grid item xs={2} sm={2}>
								<TextField
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									margin="dense"
									name="extension"
									label="Extension (if applicable)"
									type="text"
									fullWidth
									value={formData.extension}
									onChange={handleChange}
									{...getErrorProps('extension')}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									margin="dense"
									name="password"
									label="Password"
									type={
										showPasswords
											? "text"
											: "password"
									}
									fullWidth
									value={formData.password}
									onChange={handleChange}
									required
									InputProps={{

										endAdornment: (

											<InputAdornment position="end">

												<IconButton onClick={handleClickShowPasswords} edge="end">

													{showPasswords ? <VisibilityOff /> : <Visibility />}

												</IconButton>

											</InputAdornment>

										),

									}}
									{...getErrorProps('password')}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									variant='standard'
									InputLabelProps={{
										shrink: true,
										style: {
											color: "black",
										}
									}}
									margin="dense"
									name="confirmPassword"
									label="Confirm Password"
									type={
										showPasswords
											? "text"
											: "password"
									}
									fullWidth
									value={formData.confirmPassword}
									onChange={handleChange}
									required
									InputProps={{

										endAdornment: (

											<InputAdornment position="end">

												<IconButton onClick={handleClickShowPasswords} edge="end">

													{showPasswords ? <VisibilityOff /> : <Visibility />}

												</IconButton>

											</InputAdornment>

										),

									}}

									{...getErrorProps('confirmPassword')}
								/>
							</Grid>
						</Grid>
					</fieldset>
				)}

				<div style={{ "height": "3rem" }}></div>

				{/*disclaimer area*/}
				<Grid container sx={{ justifyContent: 'center', marginBottom: '1rem' }}>
					<Grid item>
						<p>By creating an account, I agree to the <a rel="noopener noreferrer" target="_blank" href="https://www.epiqglobal.com/disclaimer">Disclaimer</a>, <a target="_blank" href="/TermsOfUse">Terms of Use</a>, and <a rel="noopener noreferrer" target="_blank" href="https://www.epiqglobal.com/en-us/general/eca-privacy-policy">Privacy Statement</a>.</p>
					</Grid>
				</Grid>

				{submissionState.success &&
					<Alert severity="success" variant="filled">
						<AlertTitle>Success</AlertTitle>
						{submissionState.message}
					</Alert>
				}

				{submissionState.failure &&
					<Alert severity="error" variant="filled">
						<AlertTitle>Error</AlertTitle>
						{submissionState.message}
					</Alert>
				}
			</DialogContent>

			<DialogActions>
				<Button onClick={handleCancel} color="secondary">
					{isClosing ? "Close" : "Cancel"}
				</Button>

				{!isClosing && activeStep !== 0 && (
					<Button onClick={handleBack} color="primary">
						Back
					</Button>
				)}

				{!isClosing && (activeStep === steps.length - 1 ? (
					<Button onClick={handleSubmit} color="primary">
						Register
					</Button>
				) : (
					<Button onClick={handleNext} color="primary">
						Next
					</Button>
				))}
			</DialogActions>
		</Dialog>
	);
};

export default Register;

